// Org / data-subject export serialization — JSON + CSV, generated in-browser,
// as locked by docs/revamp/05_PORTAL_DESIGN.md (§3 "Data rights": *Export org
// data (JSON/CSV — no child PII, "there isn't any")*).
//
// The non-negotiable this module enforces — the same one the guard test asserts:
//   • A child appears in an export ONLY as a pseudonymous UUID + the owner-set
//     `displayLabel`. No name, age, guide, domain, score, or any other field
//     that could identify a child ever reaches the output. The allowlist below
//     is the single mechanism, and `buildOrgExport` runs the guard on every
//     build so the invariant cannot silently regress.
//   • Adult member emails are part of an organisation's own data and may be
//     exported; child personal data is the line we never cross.
//
// Pure TS, no dependencies. Everything is a plain value transformation: the same
// input always yields the same JSON / CSV, and nothing here performs I/O.

import type {
  Account,
  Child,
  ChildLink,
  ChildRelation,
  Consent,
  ConsentScopeKey,
  Id,
  IsoTimestamp,
  Membership,
  MembershipRole,
  Organisation,
  OrganisationType,
  Seat,
  SeatStatus,
} from "./types";
import { CHILD_ALLOWED_KEYS } from "./types";

// --- Export row shapes (flat, serialization-ready) ---------------------------

/** One org member: who they are + their org role. Adult email is org data. */
export interface MemberRow {
  accountId: Id;
  email: string;
  role: MembershipRole;
}

/** One licence seat and its current state. */
export interface SeatRow {
  accountId: Id;
  status: SeatStatus;
}

/** One child↔adult link. `childId` is a pseudonymous UUID; no child PII. */
export interface LinkRow {
  childId: Id;
  accountId: Id;
  relation: ChildRelation;
  scope: ConsentScopeKey[];
}

/** One consent-log entry (audit trail). Carries only pseudonymous ids. */
export interface ConsentRow {
  consentId: Id;
  childId: Id;
  grantedBy: Id;
  grantedAt: IsoTimestamp;
  revokedAt?: IsoTimestamp;
  scope: ConsentScopeKey[];
}

/**
 * A child as it may ever appear in an export: pseudonymous UUID + owner-set
 * label ONLY. Keys are pinned to `CHILD_ALLOWED_KEYS` from `./types` so the two
 * cannot drift apart.
 */
export type ChildRow = Pick<Child, (typeof CHILD_ALLOWED_KEYS)[number]>;

/** The full org export, ready to serialize to JSON or per-section CSV. */
export interface OrgExport {
  generatedAt: IsoTimestamp;
  organisation: { id: Id; name: string; type: OrganisationType };
  members: MemberRow[];
  seats: SeatRow[];
  links: LinkRow[];
  consentLog: ConsentRow[];
  children: ChildRow[];
}

/** The data needed to assemble an export — straight from the domain stores. */
export interface OrgExportInput {
  organisation: Organisation;
  accounts: readonly Account[];
  memberships: readonly Membership[];
  seats: readonly Seat[];
  childLinks: readonly ChildLink[];
  consents: readonly Consent[];
  children: readonly Child[];
}

// --- Allowlist (the privacy guarantee) ---------------------------------------

/**
 * The EXACT, exhaustive set of field names each export section may contain.
 * Anything outside this set is rejected by {@link assertExportSafe}. The
 * `children` section is pinned to `CHILD_ALLOWED_KEYS`, so the only child-facing
 * fields that can ever ship are the pseudonymous id, owner account id, and the
 * owner-set display label.
 */
export const EXPORT_ALLOWLIST = {
  members: ["accountId", "email", "role"],
  seats: ["accountId", "status"],
  links: ["childId", "accountId", "relation", "scope"],
  consentLog: ["consentId", "childId", "grantedBy", "grantedAt", "revokedAt", "scope"],
  children: [...CHILD_ALLOWED_KEYS],
} as const satisfies Record<string, readonly string[]>;

type ExportSection = keyof typeof EXPORT_ALLOWLIST;

const EXPORT_SECTIONS = Object.keys(EXPORT_ALLOWLIST) as ExportSection[];

/**
 * Throws if any row in any allowlisted section carries a field outside its
 * allowlist. This is the privacy backstop: if a future change tries to slip a
 * child name (or any unexpected field) into an export, the build fails loudly
 * instead of leaking. The non-row metadata (`generatedAt`, `organisation`) is
 * not user data and is not walked.
 */
export function assertExportSafe(exp: OrgExport): void {
  for (const section of EXPORT_SECTIONS) {
    const allowed: readonly string[] = EXPORT_ALLOWLIST[section];
    const rows = exp[section] as ReadonlyArray<Record<string, unknown>>;
    for (const row of rows) {
      for (const key of Object.keys(row)) {
        if (!allowed.includes(key)) {
          throw new Error(
            `Export field "${key}" in section "${section}" is not in the ` +
              `allowlist [${allowed.join(", ")}] — child PII must never reach an export.`,
          );
        }
      }
    }
  }
}

// --- Build --------------------------------------------------------------------

/**
 * Assemble an {@link OrgExport} from the domain stores, picking ONLY allowlisted
 * fields by construction, then running {@link assertExportSafe} so the result is
 * guaranteed clean before it is ever serialized.
 */
export function buildOrgExport(
  input: OrgExportInput,
  generatedAt: IsoTimestamp,
): OrgExport {
  const emailByAccount = new Map(input.accounts.map((a) => [a.id, a.email]));

  const members: MemberRow[] = input.memberships.map((m) => ({
    accountId: m.accountId,
    email: emailByAccount.get(m.accountId) ?? "",
    role: m.role,
  }));

  const seats: SeatRow[] = input.seats.map((s) => ({
    accountId: s.accountId,
    status: s.status,
  }));

  const links: LinkRow[] = input.childLinks.map((l) => ({
    childId: l.childId,
    accountId: l.accountId,
    relation: l.relation,
    scope: [...l.scope],
  }));

  const consentLog: ConsentRow[] = input.consents.map((c) => ({
    consentId: c.id,
    childId: childIdForConsent(c.id, input.childLinks),
    grantedBy: c.grantedBy,
    grantedAt: c.grantedAt,
    ...(c.revokedAt !== undefined ? { revokedAt: c.revokedAt } : {}),
    scope: [...c.scope],
  }));

  // Pick exactly the allowlisted child keys — no spread of the whole record.
  const children: ChildRow[] = input.children.map((c) => ({
    id: c.id,
    ownerAccountId: c.ownerAccountId,
    displayLabel: c.displayLabel,
  }));

  const exp: OrgExport = {
    generatedAt,
    organisation: {
      id: input.organisation.id,
      name: input.organisation.name,
      type: input.organisation.type,
    },
    members,
    seats,
    links,
    consentLog,
    children,
  };

  assertExportSafe(exp);
  return exp;
}

/** Resolve which child a consent record governs, via the child links. */
function childIdForConsent(consentId: Id, childLinks: readonly ChildLink[]): Id {
  return childLinks.find((l) => l.consentId === consentId)?.childId ?? "";
}

// --- JSON ---------------------------------------------------------------------

/**
 * Serialize an export to pretty-printed JSON. `JSON.parse` of the result
 * round-trips to a value deep-equal to `exp` (no class instances, dates, or
 * other non-JSON values are stored).
 */
export function toJson(exp: OrgExport): string {
  return JSON.stringify(exp, null, 2);
}

// --- CSV (RFC 4180) -----------------------------------------------------------

/** A value a CSV cell may hold. Arrays are joined with "; " inside one cell. */
export type CsvCell =
  | string
  | number
  | boolean
  | null
  | undefined
  | ReadonlyArray<string | number>;

/** One CSV row as a field→value map. */
export type CsvRow = Record<string, CsvCell>;

/** CRLF line terminator, per RFC 4180. */
const CSV_NEWLINE = "\r\n";

/** Render a cell value to its raw (pre-escape) string form. */
function formatCell(value: unknown): string {
  if (value === null || value === undefined) {
    return "";
  }
  if (Array.isArray(value)) {
    return value.join("; ");
  }
  return String(value);
}

/**
 * Escape one field per RFC 4180: a field containing a quote, comma, CR, or LF is
 * wrapped in double quotes with every internal `"` doubled. Everything else is
 * passed through unchanged.
 */
function escapeCsvField(field: string): string {
  if (/[",\r\n]/.test(field)) {
    return `"${field.replace(/"/g, '""')}"`;
  }
  return field;
}

/**
 * Serialize a flat list of rows to a CSV string with a header row. Columns are
 * the union of every row's keys, in first-seen order, so rows with differing /
 * optional keys (e.g. an absent `revokedAt`) align and missing cells are empty.
 * Returns "" for an empty list. Handles commas, quotes, and newlines in values.
 *
 * Accepts any object rows (e.g. the typed export row interfaces) — values are
 * read structurally and rendered via {@link formatCell}.
 */
export function toCsv(rows: ReadonlyArray<object>): string {
  if (rows.length === 0) {
    return "";
  }

  const records = rows as ReadonlyArray<Record<string, unknown>>;
  const columns: string[] = [];
  for (const row of records) {
    for (const key of Object.keys(row)) {
      if (!columns.includes(key)) {
        columns.push(key);
      }
    }
  }

  const header = columns.map(escapeCsvField).join(",");
  const body = records.map((row) =>
    columns.map((col) => escapeCsvField(formatCell(row[col]))).join(","),
  );

  return [header, ...body].join(CSV_NEWLINE);
}

/** Each export section rendered to its own CSV string. */
export interface OrgExportCsv {
  members: string;
  seats: string;
  links: string;
  consentLog: string;
  children: string;
}

/** Convenience: render every row-section of an export to CSV in one pass. */
export function toCsvBundle(exp: OrgExport): OrgExportCsv {
  return {
    members: toCsv(exp.members),
    seats: toCsv(exp.seats),
    links: toCsv(exp.links),
    consentLog: toCsv(exp.consentLog),
    children: toCsv(exp.children),
  };
}
